import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import StarField from "@/components/StarField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError("No session id in URL. Please try signing in again.");
      return;
    }

    (async () => {
      try {
        await axios.post(
          `${API}/auth/session`,
          {},
          { headers: { "X-Session-ID": sessionId } },
        );
        await refresh();
        navigate("/me", { replace: true });
      } catch (e) {
        setError("Sign-in failed. Please try again.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden">
      <StarField count={60} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#05050A] via-[#070818] to-[#05050A]" />
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 rts-glass rounded-2xl p-10 text-center max-w-md"
        data-testid="auth-callback"
      >
        {error ? (
          <>
            <p className="font-mono-rts text-[0.7rem] tracking-[0.35em] uppercase text-[#F87171]">
              Something interrupted the road
            </p>
            <p className="mt-4 text-white/80">{error}</p>
            <button
              onClick={() => navigate("/")}
              className="mt-8 rts-btn-primary"
            >
              Back home
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 text-[#EAB308] animate-spin mx-auto" />
            <p className="mt-6 font-mono-rts text-[0.7rem] tracking-[0.35em] uppercase text-[#EAB308]">
              Signing you in
            </p>
            <p className="mt-3 font-serif-rts text-2xl font-light text-white">
              A moment of quiet on the road...
            </p>
          </>
        )}
      </motion.div>
    </main>
  );
}
